import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Properties.module.scss";
import { TabPanel, Tabs } from "react-tabs";
import { H3 } from "@/lib/ui/components/Typography";
import { useTranslation } from "next-i18next";
import { RenderTabList } from "@/lib/ui/components/Gallery/GalleryModal";
import { useWindowSize } from "@/lib/hooks/useWindowSize";
import { PropertiesSort } from "./PropertiesSort";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { useFilteredProperties } from "@/lib/hooks/useFilteredProperties";
import usePagination from "@/lib/hooks/usePagination";
import { CatalogCards } from "../CatalogCards";
import { EmptyState } from "../EmptyState";
import { getWordEnding } from "@/lib/utils/getWordEnding";
import { ICatalogObject } from "@/lib/types/catalogPage";
import { useCatalogActions } from "../../../../../store/slices/catalog";
import dynamic from "next/dynamic";
import { useDebounce } from "../../../../../hooks/useDebounce";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useAppSelector } from "../../../../../store/app";
import cl from "classnames";
import { getUniqueProperties } from "../../../../../utils/getUniqueProperties";

const CatalogMapResults = dynamic(() =>
  import("../CatalogMapResults").then((c) => c.CatalogMapResults),
);

interface IProperties {
  data: ICatalogObject[];
}


export const Properties: FC<IProperties> = ({ data }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("index");
  const isFirstRender = useRef(true);
  const { query, setParam, router, paginatedQuery, deleteParam } = useCatalogFilter();
  const { setFilteredQuantity } = useCatalogActions();
  const initialPage = query.page && !isNaN(+query.page) ? +query.page : 1;

  const isTabAnimated = useRef(false);
  const { width: windowWidth } = useWindowSize();
  const [selected, setSelected] = useState(query.display === "map" ? 1 : 0);

  const onSelect = useCallback(
    (index: number) => {
      setSelected(index);
      setParam({ key: "display", value: index === 1 ? "map" : "list" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query],
  );

  const { debouncedFilteredProperties: filteredProperties, isLoading } =
    useFilteredProperties(data);
    
  const uniqueFilteredProperties = useMemo(() => {
    return getUniqueProperties(filteredProperties)
  }, [filteredProperties])

  const [hasSelectedMap, setHasSelectedMap] = useState(false);
  
  const [mapProperties, setMapProperties] = useState(filteredProperties);
  const debouncedSetMapProperties = useDebounce(
    (properties: ICatalogObject[]) => setMapProperties(properties),
    200,
  );
  useEffect(() => {
    setFilteredQuantity(uniqueFilteredProperties.length);
  }, [uniqueFilteredProperties, setFilteredQuantity]);

  const pageChangeCallback = (page: number | undefined) => {
    if (!page || page === 1) {
      deleteParam("page");
    } else {
      setParam({ key: "page", value: page }, undefined, undefined, true, true);
    }
  };

  const { getCurrentData, currentPage, jumpToPage, maxPage, setNextPage, setPrevPage } =
    usePagination(uniqueFilteredProperties, 9, initialPage, pageChangeCallback);

  useDeepCompareEffect(() => {
    if (Object.keys(paginatedQuery).length === 0) return; 
    if (query.page && query.page == "1") return;
    if (isFirstRender.current) return;
    jumpToPage(1);
  }, [paginatedQuery]);

  const propertiesData = getCurrentData();

  useEffect(() => {
    if (query.display === "map") {
      setSelected(1);
      setHasSelectedMap(true);
    }
  }, [query.display]);

  const quantity = uniqueFilteredProperties.length;
  useEffect(() => {
    if (propertiesData.length === 0 && uniqueFilteredProperties.length !== 0) {
      jumpToPage(1);
    }
  }, [uniqueFilteredProperties.length, jumpToPage, propertiesData.length]);

  useEffect(() => {
    debouncedSetMapProperties(filteredProperties);
  }, [debouncedSetMapProperties, filteredProperties]);

  const isFixedFilter = useAppSelector((state) => state.catalog.isFixedFilter);
  return (
    <Tabs
      className={cl(styles.properties, isFixedFilter ? styles["properties--fixed"] : "")}
      tabIndex={selected}
      selectedIndex={selected}
      onSelect={onSelect}
      forceRenderTabPanel={hasSelectedMap}
    >
      <div className={styles.properties__top}>
        <H3 className={styles.properties__topTitle}>
          {`${quantity} ${t(
            getWordEnding(
              quantity,
              ["declinable.offer", "declinable.offers", "declinable.offersMore"],
              language as "ru" | "en",
            ),
          )}`}
        </H3>
        <div className={styles.properties__tabs}>
          {RenderTabList(
            [t("catalog.list"), t("catalog.map")],
            isTabAnimated,
            windowWidth,
            selected,
            "dark",
          )}
        </div>
        <PropertiesSort />
      </div>
      <TabPanel>
        {selected === 0 &&
          (propertiesData.length > 0 ? (
            <CatalogCards
              isLoading={isLoading}
              propertiesData={propertiesData}
              currentPage={currentPage}
              jumpToPage={jumpToPage}
              maxPage={maxPage}
              setNextPage={setNextPage}
              setPrevPage={setPrevPage}
            />
          ) : (
            <EmptyState resetFilters={() => router.push({ pathname: router.pathname })} />
          ))}
      </TabPanel>
      <TabPanel>
        <div
          style={{
            position: selected === 1 ? "static" : "absolute",
            visibility: selected === 1 ? "visible" : "hidden",
          }}
        >
          <CatalogMapResults data={mapProperties} />
        </div>
      </TabPanel>
    </Tabs>
  );
};
